import { formatValue, humanizeMeasure, formatFromColumn } from "@/lib/reports/labels";

import type { ChartBuildCtx, ChartOption } from "./context";

export function buildGaugeOption({ data, base, measure }: ChartBuildCtx): ChartOption {
  // Single-measure radial gauge. Format inferred from registry.
  const value = data.rows.length > 0 ? Number(data.rows[0][measure] ?? 0) : 0;
  const fmt = formatFromColumn(measure, data.columns);
  const isPercent = fmt === "percent";
  const displayMax = isPercent ? 100 : Math.max(100, Math.ceil(value * 1.2));
  const displayValue = isPercent && Math.abs(value) <= 1 ? value * 100 : value;
  // Banded axis colour — green band past ~70% of max signals
  // "healthy", amber 35-70%, red <35%. Threshold-tinted progress
  // arc gives the user a target/quality read at a glance.
  const t1 = displayMax * 0.35;
  const t2 = displayMax * 0.7;
  const valueColor =
    displayValue <= t1 ? "#dc2626"
    : displayValue <= t2 ? "#f59e0b"
    : "#10b981";
  return {
    ...base,
    series: [
      {
        type: "gauge",
        // Half-circle arc (180° wide) instead of the 220° arc the
        // previous version drew. Half-circles leave the bottom flat,
        // which gives us guaranteed room directly below the geometric
        // centre for the value label without it landing off-canvas
        // (eval-gauge-team-open-rate showed an arc with no value).
        startAngle: 180,
        endAngle: 0,
        center: ["50%", "75%"],
        radius: "100%",
        min: 0,
        max: displayMax,
        splitNumber: 5,
        itemStyle: { color: valueColor },
        progress: { show: true, width: 18 },
        // Banded track shows the threshold zones behind the arc.
        axisLine: {
          lineStyle: {
            width: 18,
            color: [
              [0.35, "rgba(220,38,38,0.18)"],
              [0.7, "rgba(245,158,11,0.18)"],
              [1, "rgba(16,185,129,0.18)"],
            ],
          },
        },
        axisTick: { distance: -30, splitNumber: 5, lineStyle: { color: "#9ca3af" } },
        splitLine: { distance: -36, length: 12, lineStyle: { color: "#6b7280" } },
        axisLabel: {
          distance: -10,
          fontSize: 10,
          color: "#6b7280",
          formatter: (v: number) => formatValue(isPercent ? v / 100 : v, fmt),
        },
        anchor: { show: true, size: 14, itemStyle: { color: valueColor, borderColor: "#fff", borderWidth: 2 } },
        pointer: { show: true, length: "62%", width: 5, itemStyle: { color: valueColor } },
        title: {
          show: true,
          // Title sits beneath the value, inside the open bottom area
          // of the half-circle. Negative offset relative to the lower
          // centre keeps both labels on-canvas.
          offsetCenter: [0, "-18%"],
          fontSize: 12,
          color: "#6b7280",
        },
        detail: {
          valueAnimation: true,
          offsetCenter: [0, "-38%"],
          fontSize: 28,
          fontWeight: 700,
          color: valueColor,
          formatter: (v: number) => formatValue(isPercent ? v / 100 : v, fmt),
        },
        data: [{ value: Math.round(displayValue * 10) / 10, name: humanizeMeasure(measure) }],
      },
    ],
  } as ChartOption;
}
